import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FiClipboard, FiBarChart2, FiShoppingCart, FiPackage, FiDollarSign,
  FiCalendar, FiTruck, FiEdit3, FiFileText, FiTrash2, FiRefreshCw,
  FiTarget, FiClock, FiTrendingUp, FiUsers, FiSettings,
  FiChevronLeft, FiChevronRight, FiChevronDown, FiPower, FiAlertTriangle, FiTool, FiCheckCircle,
  FiMenu, FiX
} from 'react-icons/fi';
import { MdFactory } from 'react-icons/md';

const ANCHO_EXPANDIDO = 220;
const ANCHO_COLAPSADO = 64;
const ANCHO_PANTALLA_MOVIL = 768;

const CLAVE_DEPARTAMENTO = {
  compras: 'compras', produccion: 'produccion', mantenimiento: 'mantenimiento',
  logistica: 'logistica', calidad: 'calidad',
};

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();

  const [colapsado, setColapsado] = useState(() => localStorage.getItem('dasavena_sidebar_colapsado') === 'true');
  const [gruposAbiertos, setGruposAbiertos] = useState({});
  const [esMovil, setEsMovil] = useState(() => window.innerWidth <= ANCHO_PANTALLA_MOVIL);
  const [menuMovilAbierto, setMenuMovilAbierto] = useState(false);

  useEffect(() => {
    function alRedimensionar() {
      setEsMovil(window.innerWidth <= ANCHO_PANTALLA_MOVIL);
    }
    window.addEventListener('resize', alRedimensionar);
    return () => window.removeEventListener('resize', alRedimensionar);
  }, []);

  // Cierra el menú móvil cada vez que se navega a una pantalla nueva.
  useEffect(() => {
    if (esMovil) setMenuMovilAbierto(false);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => { logout(); navigate('/login'); };

  function toggleColapso() {
    const nuevo = !colapsado;
    setColapsado(nuevo);
    localStorage.setItem('dasavena_sidebar_colapsado', String(nuevo));
  }

  function toggleGrupo(clave) {
    setGruposAbiertos((prev) => ({ ...prev, [clave]: !prev[clave] }));
  }

  function permisoDepartamento(clave) {
    if (usuario?.rol === 'admin') return { ver_resumen: true, ver_detalle: true, capturar: true };
    const dep = (usuario?.departamentos || []).find((d) => d.clave === clave);
    return dep || { ver_resumen: false, ver_detalle: false, capturar: false };
  }
  const puedeVerGrupo = (clave) => permisoDepartamento(clave).ver_resumen;
  const puedeCapturar = (clave) => permisoDepartamento(clave).capturar;

  // En móvil el menú siempre se muestra "expandido" (nunca modo ícono-solo,
  // no tendría sentido con un panel que se desliza encima) — colapsado solo
  // aplica en escritorio.
  const mostrarColapsado = !esMovil && colapsado;

  const navItem = (active) => ({
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: mostrarColapsado ? '10px 0' : '9px 20px 9px 32px',
    justifyContent: mostrarColapsado ? 'center' : 'flex-start',
    fontSize: 13, textDecoration: 'none',
    color: active ? '#fff' : 'rgba(255,255,255,0.7)',
    borderLeft: active ? '3px solid #D6A644' : '3px solid transparent',
    background: active ? 'rgba(255,255,255,0.1)' : 'transparent',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap', overflow: 'hidden',
  });

  const grupoHeaderStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: mostrarColapsado ? '10px 0' : '12px 20px 6px',
    cursor: 'pointer', userSelect: 'none',
  };

  const seccionLabel = {
    fontSize: 10, fontWeight: 700, letterSpacing: 2,
    color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase',
    padding: mostrarColapsado ? '14px 0 6px' : '16px 20px 6px',
    textAlign: mostrarColapsado ? 'center' : 'left',
    display: 'flex', justifyContent: mostrarColapsado ? 'center' : 'flex-start', alignItems: 'center',
  };

  function GrupoKpis({ clave, titulo, Icono, items, vacio }) {
    const abierto = gruposAbiertos[clave];
    return (
      <div>
        <div style={grupoHeaderStyle} onClick={() => toggleGrupo(clave)} title={titulo}>
          {!mostrarColapsado ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.55)' }}>
              <Icono size={13} /> {titulo}
            </span>
          ) : (
            <Icono size={16} color="rgba(255,255,255,0.6)" />
          )}
          {!mostrarColapsado && <FiChevronDown size={12} style={{ color: 'rgba(255,255,255,0.4)', transform: abierto ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />}
        </div>
        {(abierto || mostrarColapsado) && (
          vacio ? (
            !mostrarColapsado && <div style={{ padding: '4px 20px 8px 32px', fontSize: 11, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>Próximamente</div>
          ) : (
            items.map((item) => (
              <Link key={item.to} to={item.to} style={navItem(pathname === item.to)} title={mostrarColapsado ? item.label : undefined}>
                <item.Icono size={15} />
                {!mostrarColapsado && <span>{item.label}</span>}
              </Link>
            ))
          )
        )}
      </div>
    );
  }

  function ItemSimple({ to, label, Icono }) {
    return (
      <Link to={to} style={navItem(pathname === to)} title={mostrarColapsado ? label : undefined}>
        <Icono size={15} />
        {!mostrarColapsado && <span>{label}</span>}
      </Link>
    );
  }

  // Mismo patrón colapsable que GrupoKpis, pero con el header dorado
  // (Captura) o gris (Configuración) que ya usábamos, para no perder esa
  // distinción visual.
  function BloqueColapsable({ clave, titulo, Icono, colorHeader, items }) {
    const abierto = gruposAbiertos[clave];
    return (
      <div style={{
        marginTop: '10px',
        background: 'rgba(0,0,0,0.14)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        paddingBottom: abierto || mostrarColapsado ? '6px' : 0,
      }}>
        <div style={grupoHeaderStyle} onClick={() => toggleGrupo(clave)} title={titulo}>
          {!mostrarColapsado ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 600, color: colorHeader }}>
              <Icono size={13} /> {titulo}
            </span>
          ) : (
            <Icono size={16} color={colorHeader} />
          )}
          {!mostrarColapsado && <FiChevronDown size={12} style={{ color: 'rgba(255,255,255,0.4)', transform: abierto ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />}
        </div>
        {(abierto || mostrarColapsado) && items.map((item) => (
          <ItemSimple key={item.to} to={item.to} label={item.label} Icono={item.Icono} />
        ))}
      </div>
    );
  }

  const ITEMS_CONFIGURACION = [
    { to: '/logistica/unidades-venta', label: 'Unidades de Venta', Icono: FiSettings, departamento: 'logistica' },
    { to: '/logistica/forecast/importar', label: 'Forecast Importar', Icono: FiSettings, departamento: 'logistica' },
    { to: '/logistica/forecast/mapeo', label: 'Forecast Mapeo', Icono: FiSettings, departamento: 'logistica' },
    { to: '/logistica/mp-desglose', label: 'Desglose MP', Icono: FiSettings, departamento: 'logistica' },
    { to: '/logistica/conversion-cajas', label: 'Conversión de Cajas', Icono: FiSettings, departamento: 'logistica' },
    { to: '/produccion/config/capacidad-maxima', label: 'Capacidad Máxima', Icono: FiSettings, departamento: 'produccion' },
    { to: '/produccion/config/mezcla', label: 'Config. Mezcla (Mz)', Icono: FiSettings, departamento: 'produccion' },
    { to: '/calidad/bpm-incidencias-catalogo', label: 'BPM Incidencias (catálogo)', Icono: FiCheckCircle, departamento: 'calidad' },
    { to: '/calidad/ppni-pnc-catalogos', label: 'Listas F-CAL-05 (PPNI/PNC)', Icono: FiCheckCircle, departamento: 'calidad' },
  ];
  const itemsConfiguracionVisibles = ITEMS_CONFIGURACION.filter((item) => puedeCapturar(item.departamento));

  return (
    <div style={{ display: 'flex' }}>
      {esMovil && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: 52,
          background: '#6A2C75', display: 'flex', alignItems: 'center', gap: 12,
          padding: '0 14px', zIndex: 1100, boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
        }}>
          <button
            onClick={() => setMenuMovilAbierto(true)}
            style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <FiMenu size={22} />
          </button>
          <img src="/logo-dasavena.png" alt="Dasavena" style={{ height: 26, objectFit: 'contain' }} />
        </div>
      )}

      {esMovil && menuMovilAbierto && (
        <div
          onClick={() => setMenuMovilAbierto(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1150 }}
        />
      )}

      <aside style={{
        width: mostrarColapsado ? ANCHO_COLAPSADO : ANCHO_EXPANDIDO,
        height: '100vh', background: '#6A2C75',
        display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0,
        boxShadow: esMovil ? '2px 0 20px rgba(0,0,0,0.4)' : '2px 0 12px rgba(106,44,117,0.3)',
        transition: esMovil ? 'transform 0.22s ease' : 'width 0.18s ease',
        transform: esMovil ? (menuMovilAbierto ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
        overflowY: 'auto', overflowX: 'hidden',
        zIndex: 1200,
      }}>
        <div style={{
          padding: mostrarColapsado ? '14px 0' : '16px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.12)',
          display: 'flex', alignItems: 'center',
          justifyContent: mostrarColapsado ? 'center' : 'space-between',
          flexShrink: 0,
        }}>
          {!mostrarColapsado && (
            <img src="/logo-dasavena.png" alt="Dasavena" style={{ width: 110, objectFit: 'contain' }} />
          )}
          {esMovil ? (
            <button
              onClick={() => setMenuMovilAbierto(false)}
              style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)', background: 'transparent', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            >
              <FiX size={16} />
            </button>
          ) : (
            <button
              onClick={toggleColapso}
              title={colapsado ? 'Expandir menú' : 'Contraer menú'}
              style={{
                width: 26, height: 26, borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)',
                background: 'transparent', color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {colapsado ? <FiChevronRight size={14} /> : <FiChevronLeft size={14} />}
            </button>
          )}
        </div>

        <nav style={{ flex: '1 1 auto', paddingBottom: '10px' }}>
          <div style={seccionLabel}>{mostrarColapsado ? <FiClipboard size={13} /> : 'Materiales'}</div>
          <ItemSimple to="/" label="Lista de Materiales" Icono={FiClipboard} />

          <div style={seccionLabel}>{mostrarColapsado ? <FiBarChart2 size={13} /> : 'KPIs'}</div>
          <ItemSimple to="/kpis" label="Resumen General" Icono={FiBarChart2} />

          {puedeVerGrupo('compras') && (
            <GrupoKpis
              clave="compras"
              titulo="Compras"
              Icono={FiShoppingCart}
              items={[
                { to: '/kpis/disponibilidad-mp', label: 'Disponibilidad MP', Icono: FiPackage },
                { to: '/kpis/precios-mp', label: 'Variabilidad de Precios', Icono: FiDollarSign },
                { to: '/kpis/dias-colocacion-oc', label: 'Días de Colocación OC', Icono: FiCalendar },
                { to: '/kpis/otif-proveedor', label: 'OTIF Proveedor', Icono: FiTruck },
              ]}
            />
          )}

          {puedeVerGrupo('produccion') && (
            <GrupoKpis
              clave="produccion"
              titulo="Producción"
              Icono={MdFactory}
              items={[
                { to: '/produccion/rechazo', label: 'Rechazo de Barras', Icono: FiAlertTriangle },
                { to: '/produccion/cumplimiento-plan', label: 'Cumplimiento del Plan', Icono: FiTarget },
                { to: '/produccion/tiempo-muerto', label: 'Tiempo Muerto', Icono: FiClock },
                { to: '/produccion/utilizacion-capacidad', label: 'Utilización de Capacidad', Icono: FiTrendingUp },
                { to: '/produccion/porcentaje-merma', label: '% Merma', Icono: FiTrash2 },
                { to: '/produccion/cobertura-plantilla', label: 'Cobertura de Plantilla', Icono: FiUsers },
              ]}
            />
          )}

          {puedeVerGrupo('mantenimiento') && (
            <GrupoKpis
              clave="mantenimiento"
              titulo="Mantenimiento"
              Icono={FiTool}
              items={[
                { to: '/mantenimiento', label: 'Tickets / Equipos / KPIs', Icono: FiTool },
              ]}
            />
          )}

          {puedeVerGrupo('logistica') && (
            <GrupoKpis
              clave="logistica"
              titulo="Logística"
              Icono={FiTruck}
              items={[
                { to: '/logistica/caducidades', label: 'Caducidades', Icono: FiPackage },
                { to: '/logistica/fillrate', label: 'Fill Rate', Icono: FiPackage },
                { to: '/logistica/otif', label: 'OTIF', Icono: FiPackage },
                { to: '/logistica/dias-inventario', label: 'Días de Inventario', Icono: FiPackage },
                { to: '/logistica/forecast', label: 'Forecast vs Real', Icono: FiPackage },
              ]}
            />
          )}

          {puedeVerGrupo('calidad') && (
            <GrupoKpis
              clave="calidad"
              titulo="Calidad"
              Icono={FiCheckCircle}
              items={[
                { to: '/calidad/acciones-correctivas', label: 'Acciones Correctivas', Icono: FiCheckCircle },
                { to: '/calidad/bpm-reporte', label: 'BPM Reporte (KPI 5.1)', Icono: FiCheckCircle },
                { to: '/calidad/quejas-cliente-reporte', label: 'Quejas de Cliente (KPI 5.2)', Icono: FiCheckCircle },
                { to: '/calidad/lotes-recepcion-reporte', label: 'Lotes Aprobados (KPI 5.4)', Icono: FiCheckCircle },
                { to: '/calidad/auditorias-reporte', label: 'Auditorías (KPI 5.5)', Icono: FiCheckCircle },
                { to: '/calidad/ppni-pnc-reporte', label: 'PPNI y PNC (KPI 5.6)', Icono: FiCheckCircle },
              ]}
            />
          )}

          {puedeCapturar('produccion') && (
            <BloqueColapsable
              clave="captura-produccion"
              titulo="Captura de Datos — Producción"
              Icono={FiEdit3}
              colorHeader="rgba(214,166,68,0.8)"
              items={[
                { to: '/produccion', label: 'Producción Diaria', Icono: FiFileText },
                { to: '/produccion/merma', label: 'Merma', Icono: FiTrash2 },
                { to: '/produccion/rechazo', label: 'Rechazo de Barras', Icono: FiAlertTriangle },
                { to: '/produccion/reproceso', label: 'Reproceso', Icono: FiRefreshCw },
              ]}
            />
          )}

          {puedeCapturar('logistica') && (
            <BloqueColapsable
              clave="captura-logistica"
              titulo="Captura de Datos — Logística"
              Icono={FiEdit3}
              colorHeader="rgba(214,166,68,0.8)"
              items={[
                { to: '/logistica/exactitud-conteo', label: 'Exactitud de Conteo', Icono: FiPackage },
                { to: '/logistica/ocupacion', label: 'Ocupación de Almacén', Icono: FiPackage },
                { to: '/logistica/peps', label: 'PEPS Auditoría', Icono: FiPackage },
                { to: '/logistica/costos-pt', label: 'Costos PT y semáforo', Icono: FiSettings, departamento: 'logistica' },
              ]}
            />
          )}

          {puedeCapturar('calidad') && (
            <BloqueColapsable
              clave="captura-calidad"
              titulo="Captura de Datos — Calidad"
              Icono={FiEdit3}
              colorHeader="rgba(214,166,68,0.8)"
              items={[
                { to: '/calidad/bpm-captura', label: 'BPM Captura', Icono: FiCheckCircle },
                { to: '/calidad/bpm-suspendidos', label: 'BPM Suspendidos', Icono: FiCheckCircle },
                { to: '/calidad/ppni-pnc-captura', label: 'PPNI / PNC Captura', Icono: FiCheckCircle },
                { to: '/calidad/ppni-pnc-pendientes', label: 'PPNI / PNC Pendientes', Icono: FiCheckCircle },
                { to: '/calidad/quejas-cliente-captura', label: 'Quejas de Cliente Captura', Icono: FiCheckCircle },
                { to: '/calidad/piezas-producidas-captura', label: 'Piezas Producidas (mensual)', Icono: FiCheckCircle },
                { to: '/calidad/lotes-recepcion-captura', label: 'Lotes Recepción Captura', Icono: FiCheckCircle },
                { to: '/calidad/auditoria-captura', label: 'Auditorías Captura', Icono: FiCheckCircle },
                
              ]}
            />
          )}

          {itemsConfiguracionVisibles.length > 0 && (
            <BloqueColapsable
              clave="configuracion"
              titulo="Configuración"
              Icono={FiSettings}
              colorHeader="rgba(255,255,255,0.5)"
              items={itemsConfiguracionVisibles}
            />
          )}

          {usuario?.rol === 'admin' && (
            <>
              <div style={seccionLabel}>{mostrarColapsado ? <FiUsers size={13} /> : 'Administración'}</div>
              <ItemSimple to="/usuarios" label="Usuarios" Icono={FiUsers} />
            </>
          )}
        </nav>

        <div style={{
          padding: mostrarColapsado ? '12px 0' : '16px 20px',
          borderTop: '1px solid rgba(255,255,255,0.12)',
          textAlign: mostrarColapsado ? 'center' : 'left',
          flexShrink: 0,
        }}>
          {!mostrarColapsado && (
            <>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                {usuario?.nombre?.split(' ')[0]}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginBottom: 10 }}>
                {usuario?.rol}
              </div>
            </>
          )}
          <button
            onClick={handleLogout}
            title="Cerrar sesión"
            style={{
              width: mostrarColapsado ? 30 : '100%', height: mostrarColapsado ? 30 : 'auto', margin: mostrarColapsado ? '0 auto' : 0,
              padding: mostrarColapsado ? 0 : '7px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)',
              background: 'transparent', color: 'rgba(255,255,255,0.6)', fontSize: 12, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            <FiPower size={13} />
            {!mostrarColapsado && <span>Cerrar sesión</span>}
          </button>
        </div>
      </aside>

      <main style={{
        marginLeft: esMovil ? 0 : (mostrarColapsado ? ANCHO_COLAPSADO : ANCHO_EXPANDIDO),
        paddingTop: esMovil ? 68 : 28,
        padding: esMovil ? '68px 16px 24px' : '28px 32px',
        minHeight: '100vh', background: '#f5f3f0', flex: 1,
        transition: 'margin-left 0.18s ease',
        boxSizing: 'border-box',
      }}>
        {children}
      </main>
    </div>
  );
}